import {useEffect,useState} from 'react';
import {motion} from 'framer-motion';
import Academy from './components/Academy';
import AbyssSection from './components/AbyssSection';
import Bloodline from './components/Bloodline';
import CharactersPage,{characters} from './components/CharactersPage';
import EntranceLoader from './components/EntranceLoader';
import Header from './components/Header';
import HeroScene from './components/HeroScene';
import SeoHead from './components/SeoHead';
import StoryPage from './components/StoryPage';
import {content} from './data/content';
import './App.css';
import './footer.css';
import './responsive.css';
import './hero-responsive.css';
import './localization.css';
import './abyss-heading.css';
import './about.css';
import './character-portrait-polish.css';
import './character-effect-adjustments.css';
import './character-navigation.css';

const routeMap={
  '':'home',
  'academy/':'academy',
  'characters/':'characters',
  'story/':'story',
  'bloodline/':'bloodline',
  'abyss/':'abyss'
};

function getRoute(){
  const parts=window.location.pathname.split('/').filter(Boolean);
  const lang=parts[0]==='en'?'en':'zh-Hant';
  const routeParts=lang==='en'?parts.slice(1):parts;
  const section=routeParts[0]||'';
  const characterId=
    section==='characters'&&routeParts[1]
      ?routeParts[1]
      :'';
  const validCharacter=characters.some(
    (item)=>item.id===characterId
  );
  const pagePath=validCharacter
    ?`characters/${characterId}/`
    :section
      ?`${section}/`
      :'';

  return{
    lang,
    characterId:validCharacter?characterId:'',
    pagePath:routeMap[pagePath]||validCharacter?pagePath:'',
    pageKey:validCharacter
      ?`character-${characterId}`
      :routeMap[pagePath]||'home'
  };
}

function getPath(lang,pagePath=''){
  return lang==='en'?`/en/${pagePath}`:`/${pagePath}`;
}

function Hero({copy,lang,onNavigate}){
  const academyPath=getPath(lang,'academy/');
  const tagline=lang==='zh-Hant'
    ?'一份鮮，一場相遇。一顆未能言明的心。'
    :copy.heroEn;

  const openAcademy=(event)=>{
    event.preventDefault();
    onNavigate(academyPath);
  };

  return(
    <section className="hero" id="top">
      <div className="hero-scene">
        <HeroScene/>
      </div>
      <motion.div
        className="hero-content"
        initial={{opacity:0,y:35}}
        animate={{opacity:1,y:0}}
        transition={{duration:1.2,delay:.3}}
      >
        <span className="kicker">{copy.heroKicker}</span>
        <h1>{copy.heroLine}</h1>
        <div className="hero-details">
          <p className="hero-en">{tagline}</p>
          <p className="hero-sub">{copy.heroSub}</p>
          <a
            className="reserve-button"
            href={academyPath}
            onClick={openAcademy}
          >
            {copy.reserve}
            <span>→</span>
          </a>
        </div>
      </motion.div>
      <a
        className="scroll-cue"
        href={academyPath}
        onClick={openAcademy}
      >
        {copy.scroll}
        <i/>
      </a>
    </section>
  );
}

function HomePage({copy,lang,onNavigate}){
  const english=lang==='en';

  return(
    <>
      <Hero
        copy={copy}
        lang={lang}
        onNavigate={onNavigate}
      />
      <div className="rule-banner">
        <b>{english?'ICHI-GO ICHI-E':'一期一會'}</b>
        <span>{copy.banner}</span>
      </div>
      <Academy copy={copy} lang={lang}/>
      <Bloodline copy={copy} lang={lang}/>
      <AbyssSection copy={copy} lang={lang}/>
    </>
  );
}

function PageContent({
  pageKey,
  characterId,
  copy,
  lang,
  onNavigate
}){
  if(pageKey==='academy'){
    return <Academy copy={copy} lang={lang}/>;
  }

  if(pageKey==='characters'||characterId){
    return(
      <CharactersPage
        lang={lang}
        characterId={characterId}
        onNavigate={onNavigate}
      />
    );
  }

  if(pageKey==='story'){
    return <StoryPage lang={lang}/>;
  }

  if(pageKey==='bloodline'){
    return <Bloodline copy={copy} lang={lang}/>;
  }

  if(pageKey==='abyss'){
    return <AbyssSection copy={copy} lang={lang}/>;
  }

  return(
    <HomePage
      copy={copy}
      lang={lang}
      onNavigate={onNavigate}
    />
  );
}

export default function App(){
  const initialRoute=getRoute();
  const [route,setRoute]=useState(initialRoute);
  const [entranceVisible,setEntranceVisible]=useState(
    initialRoute.pageKey==='home'
  );
  const {lang,pagePath,pageKey,characterId}=route;
  const copy=content[lang];
  const english=lang==='en';
  const isHome=pageKey==='home';

  useEffect(()=>{
    const handlePopState=()=>setRoute(getRoute());
    window.addEventListener('popstate',handlePopState);

    return()=>{
      window.removeEventListener('popstate',handlePopState);
    };
  },[]);

  const navigate=(path,options={})=>{
    const {preserveScroll=false}=options;
    const currentScroll=window.scrollY;

    if(window.location.pathname!==path){
      window.history.pushState({},'',path);
    }

    setRoute(getRoute());

    if(preserveScroll){
      window.requestAnimationFrame(()=>{
        window.scrollTo({
          top:currentScroll,
          left:0,
          behavior:'auto'
        });
      });
      return;
    }

    window.scrollTo({
      top:0,
      left:0,
      behavior:'auto'
    });
  };

  return(
    <>
      <SeoHead lang={lang} pageKey={pageKey}/>
      <EntranceLoader
        visible={entranceVisible}
        copy={copy}
        lang={lang}
        onComplete={()=>setEntranceVisible(false)}
      />
      <Header
        lang={lang}
        pagePath={pagePath}
        onNavigate={navigate}
      />
      <main className={isHome?'':'standalone-page'}>
        <PageContent
          pageKey={pageKey}
          characterId={characterId}
          copy={copy}
          lang={lang}
          onNavigate={navigate}
        />
      </main>
      <footer>
        <a
          className="footer-brand"
          href={english?'/en/':'/'}
          onClick={(event)=>{
            event.preventDefault();
            navigate(english?'/en/':'/');
          }}
        >
          OMAKASE<span>.HK</span>
          {english
            ?' · SHUNSHIN RYOTEI · SECRET ITAMAE ACADEMY'
            :' · 旬心料亭 · 裏・板前學院'}
        </a>
        <p>{copy.footer}</p>
      </footer>
    </>
  );
}